import {
  createClient,
  createLDReactProviderWithClient,
} from '@launchdarkly/react-sdk';
import { backendAuthApiRef } from '@internal/plugin-neo4j-auth-common';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { FC, ReactNode, useEffect, useState } from 'react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { useCaptureSearchEvent } from '@internal/plugin-neo4j-analytics';

import { LDTestingProvider } from '../../testing/LDTestingProvider';

const LAUNCHDARKLY_START_TIMEOUT_SECONDS = 5;

export function RootProviders({ children }: { children: ReactNode }) {
  const configApi = useApi(configApiRef);

  /**
   * Resolve the backend auth API at app root so its constructor runs, starting
   * token warming, before the first page loads, rather than lazily on the first
   * page that happens to consume the API.
   */
  useApi(backendAuthApiRef);

  const [LaunchDarklyProvider, setLaunchDarklyProvider] = useState<FC<{
    children: ReactNode;
  }> | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function initializeLaunchDarkly() {
      const auth = configApi.getOptional('auth');
      const custom = configApi.getOptional('custom');
      let launchDarklyClientId = '';
      let environment = '';

      if (
        typeof auth === 'object' &&
        !Array.isArray(auth) &&
        typeof custom === 'object' &&
        !Array.isArray(custom)
      ) {
        const launchDarklyClientIdTmp = custom?.launchDarklyClientId;
        const authTmp = auth?.environment;

        if (typeof launchDarklyClientIdTmp === 'string') {
          launchDarklyClientId = launchDarklyClientIdTmp;
        }

        if (typeof authTmp === 'string') {
          environment = authTmp;
        }
      }

      let Provider: typeof LaunchDarklyProvider;
      if (environment === 'ci') {
        Provider = LDTestingProvider;
      } else {
        try {
          const client = createClient(
            launchDarklyClientId,
            { kind: 'aura-portal-user', anonymous: true },
            { applicationInfo: { id: 'aura-portal' } },
          );
          const result = await client.start({
            timeout: LAUNCHDARKLY_START_TIMEOUT_SECONDS,
          });
          if (result.status !== 'complete') {
            // eslint-disable-next-line no-console
            console.error(
              'LaunchDarkly client did not finish starting',
              result.status,
            );
          }
          Provider = createLDReactProviderWithClient(client);
        } catch (error) {
          // createClient can throw before start settles, and the portal still has to render.
          // eslint-disable-next-line no-console
          console.error('LaunchDarkly client failed to start', error);
          Provider = UnavailableLaunchDarklyProvider;
        }
      }

      if (isMounted) {
        setLaunchDarklyProvider(() => Provider);
      }
    }

    initializeLaunchDarkly();

    return () => {
      isMounted = false;
    };
  }, [configApi]);

  if (!LaunchDarklyProvider) {
    return null;
  }

  return (
    <NuqsAdapter>
      <LaunchDarklyProvider>
        <SearchContextProvider>
          <InternalRoot>{children}</InternalRoot>
        </SearchContextProvider>
      </LaunchDarklyProvider>
    </NuqsAdapter>
  );
}

function InternalRoot({ children }: { children: ReactNode }) {
  useCaptureSearchEvent();

  return <>{children}</>;
}

function UnavailableLaunchDarklyProvider({
  children,
}: {
  children: ReactNode;
}) {
  return <>{children}</>;
}
